import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Component, NgModule } from '@angular/core';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { WindmillTooltipDirective, WindmillTooltipModule } from '@windmill/ng-windmill/tooltip';

const windmillComponents = [CentricIcon2Component, WindmillTooltipDirective];
/**
 * To use this component in a module, you can import the `CentricChipsModule`:
 * @example
 * ```typescript
 * import { CentricChipsModule } from '@windmill/ng-windmill/chips';
 * ```
 *
 * To use the standalone component directly, you can import `CentricChipComponent`:
 * @example
 * ```typescript
 * import { CentricChipComponent } from '@windmill/ng-windmill/chips';
 * ```
 */
class CentricChipComponent {
    /**
     * Used for displaying the message for title and for component
     */
    message;
    /**
     * The size of the chips
     */
    size = 'normal';
    /**
     * If true, the component will have tooltip
     */
    hasTooltip = false;
    /**
     * The text for the tooltip
     */
    tooltipText = 'Delete';
    /**
     * If true, the component will have title
     */
    hasTitle = false;
    /**
     * Indicates whether the chip component should apply ellipsis to its text content.
     * When set to true, any text that overflows the chip's boundaries will be truncated with an ellipsis (...)
     */
    hasEllipsis = false;
    /**
     * Determines whether the chip component should have a predefined maximum width.
     * When set to true, the chip will have a max-width, preventing it from expanding beyond this width.
     * This helps maintain a consistent and controlled appearance for the chip, especially when dealing with variable text lengths.
     */
    hasMaxWidth = true;
    /**
     * The readonly state for the component
     */
    readonly = false;
    /**
     * Sets the aria-label attribute for the delete button
     */
    ariaLabelDeleteButton;
    /**
     * Emits when delete a chip
     */
    deleteChip = new EventEmitter();
    removeItem() {
        this.deleteChip.emit(true);
    }
    get tooltipTextValue() {
        return this.hasTooltip ? this.tooltipText : '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricChipComponent, isStandalone: true, selector: "centric-chip", inputs: { message: "message", size: "size", hasTooltip: "hasTooltip", tooltipText: "tooltipText", hasTitle: "hasTitle", hasEllipsis: "hasEllipsis", hasMaxWidth: "hasMaxWidth", readonly: "readonly", ariaLabelDeleteButton: "ariaLabelDeleteButton" }, outputs: { deleteChip: "deleteChip" }, ngImport: i0, template: "<div\r\n  role=\"listitem\"\r\n  class=\"centric-chip-container\"\r\n  [class.small]=\"size === 'small'\"\r\n  [class.has-ellipsis]=\"hasEllipsis\"\r\n  [class.has-max-width]=\"hasMaxWidth\">\r\n  <div class=\"chip-content\">\r\n    <span [attr.title]=\"hasTitle ? message : ''\">\r\n      {{ message }}\r\n    </span>\r\n    @if (!readonly) {\r\n      <div class=\"button-container\">\r\n        <button\r\n          class=\"chip-delete-button\"\r\n          type=\"button\"\r\n          windmill-tooltip\r\n          [tooltipText]=\"tooltipTextValue\"\r\n          [tooltipPosition]=\"'above'\"\r\n          [tooltipColor]=\"'neutral'\"\r\n          [isTooltipDisabled]=\"!hasTooltip\"\r\n          [attr.aria-label]=\"ariaLabelDeleteButton\"\r\n          (click)=\"removeItem()\">\r\n          <centric-icon2\r\n            [svgIcon]=\"'cancel_r'\"\r\n            [size]=\"'16'\"></centric-icon2>\r\n        </button>\r\n      </div>\r\n    }\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "directive", type: WindmillTooltipDirective, selector: "[windmill-tooltip]", inputs: ["tooltipText", "tooltipPosition", "tooltipShowDelay", "tooltipHideDelay", "tooltipClassName", "tooltipColor", "isTooltipDisabled"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-chip', imports: [CommonModule, ...windmillComponents], standalone: true, template: "<div\r\n  role=\"listitem\"\r\n  class=\"centric-chip-container\"\r\n  [class.small]=\"size === 'small'\"\r\n  [class.has-ellipsis]=\"hasEllipsis\"\r\n  [class.has-max-width]=\"hasMaxWidth\">\r\n  <div class=\"chip-content\">\r\n    <span [attr.title]=\"hasTitle ? message : ''\">\r\n      {{ message }}\r\n    </span>\r\n    @if (!readonly) {\r\n      <div class=\"button-container\">\r\n        <button\r\n          class=\"chip-delete-button\"\r\n          type=\"button\"\r\n          windmill-tooltip\r\n          [tooltipText]=\"tooltipTextValue\"\r\n          [tooltipPosition]=\"'above'\"\r\n          [tooltipColor]=\"'neutral'\"\r\n          [isTooltipDisabled]=\"!hasTooltip\"\r\n          [attr.aria-label]=\"ariaLabelDeleteButton\"\r\n          (click)=\"removeItem()\">\r\n          <centric-icon2\r\n            [svgIcon]=\"'cancel_r'\"\r\n            [size]=\"'16'\"></centric-icon2>\r\n        </button>\r\n      </div>\r\n    }\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { message: [{
                type: Input
            }], size: [{
                type: Input
            }], hasTooltip: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], hasTitle: [{
                type: Input
            }], hasEllipsis: [{
                type: Input
            }], hasMaxWidth: [{
                type: Input
            }], readonly: [{
                type: Input
            }], ariaLabelDeleteButton: [{
                type: Input
            }], deleteChip: [{
                type: Output
            }] } });

const UPPER_LIMIT = 99;
const LOWER_LIMIT = 0;
/**
 * To use this component in a module, you can import the `CentricChipsModule`:
 * @example
 * ```typescript
 * import { CentricChipsModule } from '@windmill/ng-windmill/chips';
 * ```
 *
 * To use the standalone component directly, you can import `CentricChipsListComponent`:
 * @example
 * ```typescript
 * import { CentricChipsListComponent } from '@windmill/ng-windmill/chips';
 * ```
 */
class CentricChipsListComponent {
    /**
     * If set to true, the property adds a 'More' button inside the chips list
     */
    hasMoreButton = false;
    /**
     * Sets the size of the 'More' button
     */
    moreButtonSize = 'normal';
    /**
     * Sets the number of leftover chips inside the 'More' button
     */
    get moreButtonContent() {
        return this._moreButtonContent;
    }
    set moreButtonContent(value) {
        this._moreButtonContent = value && value > UPPER_LIMIT ? UPPER_LIMIT : value;
    }
    /**
     * If set to true, the property applies a tooltip on the 'More' button
     */
    moreButtonHasTooltip = true;
    /**
     * Sets the text for the tooltip applied on the 'More' button
     */
    moreButtonTooltipText = 'View remaining chips';
    /**
     * Sets the aria-label attribute
     */
    ariaLabel;
    /**
     * Sets the aria-labelledby attribute
     */
    ariaLabelledby;
    /**
     * Sets the aria-description attribute
     */
    ariaDescription;
    /**
     * Sets the aria-describedby attribute on the 'More' button
     */
    ariaDescribedbyMoreButton;
    /**
     * Sets the aria-description attribute on the 'More' button
     */
    ariaDescriptionMoreButton;
    /**
     * Emits an event when the 'More' button is clicked
     */
    moreButtonClick = new EventEmitter();
    _moreButtonContent;
    get shouldShowMoreButton() {
        return this.hasMoreButton && Number(this.moreButtonContent) > LOWER_LIMIT;
    }
    onClick(event) {
        this.moreButtonClick.emit(event);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricChipsListComponent, isStandalone: true, selector: "centric-chips-list", inputs: { hasMoreButton: "hasMoreButton", moreButtonSize: "moreButtonSize", moreButtonContent: "moreButtonContent", moreButtonHasTooltip: "moreButtonHasTooltip", moreButtonTooltipText: "moreButtonTooltipText", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedbyMoreButton: "ariaDescribedbyMoreButton", ariaDescriptionMoreButton: "ariaDescriptionMoreButton" }, outputs: { moreButtonClick: "moreButtonClick" }, ngImport: i0, template: "<div\r\n  class=\"centric-chips-list\"\r\n  role=\"list\"\r\n  [attr.aria-label]=\"ariaLabel\"\r\n  [attr.aria-description]=\"ariaDescription\"\r\n  [attr.aria-labelledby]=\"ariaLabelledby\">\r\n  <ng-content></ng-content>\r\n  @if (shouldShowMoreButton) {\r\n    @if (moreButtonHasTooltip) {\r\n      <button\r\n        class=\"more-button {{ moreButtonSize }}\"\r\n        type=\"button\"\r\n        windmill-tooltip\r\n        [tooltipText]=\"moreButtonTooltipText\"\r\n        [tooltipPosition]=\"'above'\"\r\n        [tooltipColor]=\"'neutral'\"\r\n        (click)=\"onClick($event)\">\r\n        +{{ moreButtonContent }}\r\n      </button>\r\n    } @else {\r\n      <button\r\n        class=\"more-button {{ moreButtonSize }}\"\r\n        type=\"button\"\r\n        [attr.aria-describedby]=\"ariaDescribedbyMoreButton\"\r\n        [attr.aria-description]=\"ariaDescriptionMoreButton\"\r\n        (click)=\"onClick($event)\">\r\n        +{{ moreButtonContent }}\r\n      </button>\r\n    }\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: WindmillTooltipDirective, selector: "[windmill-tooltip]", inputs: ["tooltipText", "tooltipPosition", "tooltipShowDelay", "tooltipHideDelay", "tooltipClassName", "tooltipColor", "isTooltipDisabled"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-chips-list', imports: [CommonModule, WindmillTooltipDirective], standalone: true, template: "<div\r\n  class=\"centric-chips-list\"\r\n  role=\"list\"\r\n  [attr.aria-label]=\"ariaLabel\"\r\n  [attr.aria-description]=\"ariaDescription\"\r\n  [attr.aria-labelledby]=\"ariaLabelledby\">\r\n  <ng-content></ng-content>\r\n  @if (shouldShowMoreButton) {\r\n    @if (moreButtonHasTooltip) {\r\n      <button\r\n        class=\"more-button {{ moreButtonSize }}\"\r\n        type=\"button\"\r\n        windmill-tooltip\r\n        [tooltipText]=\"moreButtonTooltipText\"\r\n        [tooltipPosition]=\"'above'\"\r\n        [tooltipColor]=\"'neutral'\"\r\n        (click)=\"onClick($event)\">\r\n        +{{ moreButtonContent }}\r\n      </button>\r\n    } @else {\r\n      <button\r\n        class=\"more-button {{ moreButtonSize }}\"\r\n        type=\"button\"\r\n        [attr.aria-describedby]=\"ariaDescribedbyMoreButton\"\r\n        [attr.aria-description]=\"ariaDescriptionMoreButton\"\r\n        (click)=\"onClick($event)\">\r\n        +{{ moreButtonContent }}\r\n      </button>\r\n    }\r\n  }\r\n</div>\r\n" }]
        }], propDecorators: { hasMoreButton: [{
                type: Input
            }], moreButtonSize: [{
                type: Input
            }], moreButtonContent: [{
                type: Input
            }], moreButtonHasTooltip: [{
                type: Input
            }], moreButtonTooltipText: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedbyMoreButton: [{
                type: Input
            }], ariaDescriptionMoreButton: [{
                type: Input
            }], moreButtonClick: [{
                type: Output
            }] } });

const windmillModules = [CentricIcon2Module, WindmillTooltipModule];
const moduleComponents = [CentricChipComponent, CentricChipsListComponent];
class CentricChipsModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsModule, imports: [CommonModule, CentricIcon2Module, WindmillTooltipModule, CentricChipComponent, CentricChipsListComponent], exports: [CentricChipComponent, CentricChipsListComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsModule, imports: [CommonModule, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricChipsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricChipComponent, CentricChipsListComponent, CentricChipsModule };
